import { isEqual } from 'lodash-es'
import {
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { SessionTrait, TraitValue } from 'types'
import { sleep } from 'utils'
import { makeSelection } from '../../utils/selectionUtils'
import { IFormStep, makeTraitPanels, TraitWithValues } from '../../utils/traitUtils'
import { ISessionContext, SessionContext, useExplorerConfig } from '../providers'
import { SCheckbox, SCheckboxProps, SText, SView } from '../scaled'
import AnimatedPanel from './AnimatedPanel'
import InputTraitSteps from './InputTraitSteps'
import FormDone from './TraitSelectionDone'
import TraitSelector from './TraitSelector'

interface Props extends FormStepProps {}

interface FormStepProps {
  selections: SessionTrait[]
  setSelections: Dispatch<SetStateAction<SessionTrait[]>>
}

export default function FormStep(props: Props) {
  const { templateDocs } = useExplorerConfig()
  const { getLanguageValue, routeSteps, status } = useContext(SessionContext)
  const [showDone, setShowDone] = useState(false)

  const steps = useMemo(() => makeTraitPanels(templateDocs, routeSteps), [templateDocs, routeSteps])
  const currentStepType = routeSteps[routeSteps.length - 1]?.type

  useEffect(() => {
    if (status !== 'active' || currentStepType === 'trait') return setShowDone(false)
    sleep(400).then(() => setShowDone(true))
  }, [currentStepType, status])

  return (
    <SView flex={1} h="100%">
      {steps.map(step => (
        <AnimatedStep getLanguageValue={getLanguageValue} {...props} {...step} key={step.key} />
      ))}
      <InputTraitSteps />
      <AnimatedPanel position={currentStepType === 'layout' ? 'current' : 'pending'}>
        <FormDone show={showDone} />
      </AnimatedPanel>
    </SView>
  )
}

interface AnimatedStepProps extends Props, IFormStep {
  getLanguageValue: ISessionContext['getLanguageValue']
}

function AnimatedStep({ group, selections, setSelections, position, ...rest }: AnimatedStepProps) {
  const memoGroup = useRef(group)
  if (!isEqual(memoGroup.current, group)) memoGroup.current = group

  const traits = memoGroup.current.values ?? []
  const stepSelections = filterSelections(selections, traits)
  const memoSelections = useRef(stepSelections)
  if (!isEqual(memoSelections.current, stepSelections)) memoSelections.current = stepSelections

  useEffect(() => {
    if (position !== 'current') return

    const values = memoGroup.current.values
    const validSelections = filterSelections(memoSelections.current, values)
    if (!isEqual(memoSelections.current, validSelections)) onChange(validSelections)
  }, [memoSelections.current])

  const onChange = useCallback(
    (value: SessionTrait[]) => {
      if (!memoGroup.current) return () => {}

      setSelections(s => [...filterSelections(s, memoGroup.current.values, true), ...value])
    },
    [memoGroup.current]
  )

  function filterSelections(selections: SessionTrait[], values: TraitValue[], other?: boolean) {
    return selections.filter(s => {
      const hasSelection = values.some(v => s.title === rest.getLanguageValue(v.title))
      return other ? !hasSelection : hasSelection
    })
  }

  return (
    <AnimatedPanel flex={1} position={position}>
      <StepContent
        group={memoGroup.current}
        selections={memoSelections.current}
        onChange={onChange}
        {...rest}
      />
    </AnimatedPanel>
  )
}

interface StepContentProps {
  group: TraitWithValues
  getLanguageValue: ISessionContext['getLanguageValue']
  selections: SessionTrait[]
  onChange: (value: SessionTrait[]) => void
}

const StepContent = memo(({ getLanguageValue, group, selections, onChange }: StepContentProps) => {
  const { isMulti, title, titleDisplay, helpText, values } = group

  function handleButton(trait: TraitValue) {
    const selection = makeSelection(trait, title!, getLanguageValue)
    let updated = selections.filter(s => !isEqual(s, selection))
    if (selections.length === updated.length) {
      if (isMulti) updated.push(selection)
      else updated = [selection]
    } else if (!isMulti && updated.length === 0) {
      updated = [selection]
    }
    onChange(updated)
  }

  function handleCheck() {
    if (selections.length === values.length) {
      onChange([])
    } else {
      onChange(values.map(v => makeSelection(v, title!, getLanguageValue)))
    }
  }

  return (
    <SView flex={1} overflow="hidden" gap={10}>
      <SView gap={5}>
        <SText fontSize={35}>{getLanguageValue(titleDisplay || title)}</SText>
        <HelpSelect
          helpText={
            getLanguageValue(helpText) ||
            `Select the option${isMulti ? '(s)' : ''} that best describes you`
          }
          onChange={handleCheck}
          state={isMulti ? (selections?.length ?? 0) / values.length : undefined}
        />
      </SView>

      <TraitSelector
        group={group}
        getLanguageValue={getLanguageValue}
        onSelect={handleButton}
        selections={selections || []}
      />
    </SView>
  )
})

interface HelpSelectProps extends Omit<SCheckboxProps, 'checked'> {
  /** (show multi-check if provided) 0-1 empty to fully selected */
  state?: number
  helpText?: string
}

function HelpSelect({ state, helpText, ...rest }: HelpSelectProps) {
  return (
    <SView direction="row" justifyContent="space-between" alignItems="center">
      <SText fontSize={18} fitToWidth="100%" fitToHeight={25}>
        {helpText ||
          (state != null
            ? 'Select one or more items'
            : 'Select an the option that best describes you')}
      </SText>
      {state != null && (
        <SCheckbox
          checked={Boolean(state) && (state < 1 ? 'indeterminate' : true)}
          children="Select all"
          {...rest}
        />
      )}
    </SView>
  )
}
